import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { Skeleton } from "../components/Skeleton/Skeleton";
import { getUpdates, type Update } from "../api/updates";
import { postJoin } from "../api/waitlist";
import appStyles from "../styles/AppClasses.module.scss";
import styles from "./UpdatesPage.stylesheet.module.scss";

const UpdatesListSkeleton = () => (
  <ul
    className={classNames(styles.timelineList, appStyles.scrollReveal)}
    aria-label="Loading past updates"
  >
    {Array.from({ length: 4 }, (_, index) => (
      <li key={index} className={styles.timelineItem}>
        <div className={styles.timelineItemSkeleton}>
          <Skeleton height={14} width="min(160px, 42%)" />
          <Skeleton height={20} width={index % 2 === 0 ? "72%" : "58%"} />
        </div>
      </li>
    ))}
  </ul>
);

export const UpdatesPage = () => {
  const [updates, setUpdates] = useState<Update[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });

    const loadUpdates = async () => {
      const nextUpdates = await getUpdates();
      setUpdates(nextUpdates);
      setLoading(false);
    };

    loadUpdates();
  }, []);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleSubscribe = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const subscriberName = name.trim();
    const subscriberEmail = email.trim();
    if (!subscriberName || !subscriberEmail) return;

    setSubscriptionStatus("loading");
    void postJoin(subscriberName, subscriberEmail).then(
      () => {
        setSubscriptionStatus("success");

        const analyticsWindow = window as typeof window & {
          Shopify?: {
            analytics?: {
              publish?: (
                eventName: string,
                eventData: Record<string, unknown>,
              ) => boolean | Promise<boolean>;
              visitor?: (visitorData: {
                email: string;
              }) => boolean | Promise<unknown>;
            };
          };
          fbq?: (...args: unknown[]) => void;
        };
        const eventData = {
          signup_method: "email",
          source: "updates_page",
        };

        analyticsWindow.Shopify?.analytics?.publish?.(
          "replay:email_subscribed",
          eventData,
        );
        analyticsWindow.Shopify?.analytics?.visitor?.({
          email: subscriberEmail,
        });
        analyticsWindow.fbq?.("track", "Lead", {
          content_name: "Email subscription",
          content_category: "Waitlist",
          source: "updates_page",
        });
        window.dispatchEvent(
          new CustomEvent("replay:email_subscribed", { detail: eventData }),
        );
      },
      () => setSubscriptionStatus("error"),
    );
  };

  return (
    <div className={appStyles.appContainer}>
      <div
        className={classNames(
          appStyles.landing,
          appStyles.timelineLanding,
          styles.updatesPage,
        )}
      >
        <header
          className={classNames(styles.updatesIntro, appStyles.scrollReveal)}
        >
          <h1 className={styles.updatesTitle}>Updates</h1>
          <p className={styles.updatesSubtitle}>
            Replay is being built fully in public. Follow along!
          </p>
          <form className={styles.subscribeForm} onSubmit={handleSubscribe}>
            <div className={styles.subscribeFields}>
              <label className={styles.subscribeField}>
                <span>Name</span>
                <input
                  type="text"
                  name="name"
                  autoComplete="name"
                  placeholder="Your name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                  disabled={subscriptionStatus === "success"}
                />
              </label>
              <label className={styles.subscribeField}>
                <span>Email</span>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  disabled={subscriptionStatus === "success"}
                />
              </label>
              <button
                className={styles.subscribeButton}
                type="submit"
                disabled={
                  subscriptionStatus === "loading" ||
                  subscriptionStatus === "success"
                }
              >
                {subscriptionStatus === "loading"
                  ? "Subscribing…"
                  : subscriptionStatus === "success"
                    ? "Subscribed"
                    : "Subscribe"}
              </button>
            </div>
            <p className={styles.subscribeNote}>
              Weekly product updates. Unsubscribe anytime.
            </p>
            <p
              className={classNames(styles.subscribeStatus, {
                [styles.subscribeSuccess]: subscriptionStatus === "success",
                [styles.subscribeError]: subscriptionStatus === "error",
              })}
              role="status"
              aria-live="polite"
            >
              {subscriptionStatus === "success" &&
                "You're subscribed. Watch your inbox for Replay updates."}
              {subscriptionStatus === "error" &&
                "Something went wrong. Please try again."}
            </p>
          </form>
        </header>
        <h2
          className={classNames(
            styles.pastUpdatesTitle,
            appStyles.scrollReveal,
          )}
        >
          Past Updates
        </h2>
        {loading && <UpdatesListSkeleton />}
        {!loading && updates.length === 0 && (
          <p
            className={classNames(
              appStyles.timelineEmpty,
              appStyles.scrollReveal,
            )}
          >
            No updates yet.
          </p>
        )}
        {!loading && updates.length > 0 && (
          <ul
            className={classNames(styles.timelineList, appStyles.scrollReveal)}
          >
            {updates.map((u) => (
              <li key={u.id} className={styles.timelineItem}>
                <Link
                  to={`/updates/${u.slug}`}
                  className={styles.timelineItemLink}
                >
                  <span className={styles.timelineItemDate}>
                    {formatDate(u.published_at)}
                  </span>
                  <span className={styles.timelineItemTitle}>{u.title}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
